import React from 'react';
import { Box } from '@mui/material';
import { colors } from '@sweep-io/sweep-design';
import { useResizeDrag } from '../../../hooks/useResizeDrag';
import { InlinePanel, InlinePanelProps } from './InlinePanel';

export interface ResizableInlinePanelProps extends Omit<InlinePanelProps, 'width' | 'sx'> {
  panelWidth: number;
  onResize: (width: number) => void;
  minWidth: number;
  maxWidth: number;
  sx?: React.ComponentProps<typeof Box>['sx'];
}

/**
 * Resizable inline panel component - extends InlinePanel with resize handle on the left edge.
 */
export const ResizableInlinePanel: React.FC<ResizableInlinePanelProps> = ({
  panelWidth,
  onResize,
  minWidth,
  maxWidth,
  onClose,
  header,
  children,
  showHeaderBorder,
  sx,
}) => {
  const {
    isResizing,
    isHovering: isHoveringHandle,
    handleResizeStart,
    handleMouseEnter,
    handleMouseLeave,
  } = useResizeDrag({
    onResize,
    minValue: minWidth,
    maxValue: maxWidth,
    calculateDiff: (e: MouseEvent, startX: number) => {
      return startX - e.clientX;
    },
  });

  return (
    <InlinePanel
      width={panelWidth}
      onClose={onClose}
      header={header}
      showHeaderBorder={showHeaderBorder}
      sx={sx}
    >
      {/* Drag overlay - prevents content interactions during resize */}
      {isResizing && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 500,
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            pointerEvents: 'all',
          }}
        />
      )}

      {/* Resize Handle */}
      <Box
        className="panel-resize-handle"
        sx={{
          position: 'absolute',
          left: '-2px',
          top: 0,
          bottom: 0,
          width: '6px',
          cursor: 'col-resize',
          zIndex: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover .resize-indicator': {
            opacity: 1,
            backgroundColor: colors.blue[500],
          },
        }}
        onMouseDown={(e) => handleResizeStart(e, panelWidth)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Box
          className="resize-indicator"
          sx={{
            width: '1px',
            height: '100%',
            transition: 'opacity 0.2s ease-in-out, background-color 0.2s ease-in-out',
            backgroundColor: isHoveringHandle || isResizing ? colors.blue[500] : 'transparent',
            opacity: isHoveringHandle || isResizing ? 1 : 0,
          }}
        />
      </Box>

      {children}
    </InlinePanel>
  );
};

export default ResizableInlinePanel;
